import { format, parse } from 'date-fns'
import CONSTANTS from '../../Constants'
import { API_CONFIG, API_LIST } from '../../Constants/ApiDefinations'
import { CONFIG } from '../../Constants/Constant/Configs'
import { PAGE_URL, PageNames } from '../../Constants/PagesRoutes/PageURLs'
import { isJobCancelledBeforeStart } from '../../Helpers/BulkUpload'
import { TIPS_MODULE } from '../../Helpers/ManageTips'
import { downloadBulkUpdateDataFunction, getDateInFormat } from '../../Helpers/Util'
import mainApiService from '../../Services'
import { showAlert } from '../../Store/Slice/alert.slice'
import { setcancelBulkUpdate } from '../../Store/Slice/bulkUpdate.slice'
import { fetchMasterData } from '../../Store/Slice/master-data.slice'
import { BulkUploadCancelIcon, DownloadIcon } from '../Icons/PlayListPlay'
import MUITooltip from '../Shared/MUI-Tooltip'
import Style from './BulkupdateStyle.module.scss'

interface BulkOperationsConfig {
  tooltipModule: string
  backButtonLabel: string
  backButtonUrl: string
}

export const getBulkOperationsConfig = (
  bulkUpdateType:
    | 'bulk-update'
    | 'bulk-state-change'
    | 'interactive-bulk-update'
    | 'bulk-transcoding'
    | string
    | undefined,
  pageType: 'view' | string | undefined, // Extend as needed,
): BulkOperationsConfig => {
  let config: BulkOperationsConfig

  switch (bulkUpdateType) {
    case 'bulk-state-change':
      config = {
        tooltipModule: TIPS_MODULE.BULK_STATE_CHANGE,
        backButtonLabel: CONSTANTS.BULK_OPS.BULK_STATE_CHANGE,
        backButtonUrl: PAGE_URL[PageNames.BulkStateChange],
      }
      break
    case 'interactive-bulk-update':
      config = {
        tooltipModule: TIPS_MODULE.INTERACTIVE_BULK_UPDATE,
        backButtonLabel: CONSTANTS.BULK_OPS.INTERACTIVE_BULK_UPDATE,
        backButtonUrl: PAGE_URL[PageNames.InteractiveBulkUpdate],
      }
      break
    case 'bulk-transcoding':
      config = {
        tooltipModule: TIPS_MODULE.BULK_TRANSCODING,
        backButtonLabel: CONSTANTS.BULK_OPS.BULK_TRANSCODING,
        backButtonUrl: PAGE_URL[PageNames.BulkTranscoding],
      }
      break
    default:
      config = {
        tooltipModule: TIPS_MODULE.BULK_UPDATE,
        backButtonLabel: CONSTANTS.BULK_OPS.BULK_UPDATE,
        backButtonUrl: PAGE_URL[PageNames.BlukUpdate],
      }
      break
  }

  if (pageType === CONSTANTS.PAGE_TYPE.VIEW) {
    config.backButtonLabel = CONSTANTS.BULK_OPS.VIEW_JOB
  }

  return config
}

interface GenerateBulkOperationsJobData {
  attributes?: { label: string }[]
  targetState?: string
  jobId: string
  assetCount: number
  userName: string
  scheduleDate: string
  endDate: string
  successCount: number
  failureCount: number
  blitzIds?: string[]
  requestData?: {
    blitzIds?: string[]
    targetState?: string
    attributes?: any[]
    processType?: string
  }
  masterDataWithFieldName?: Record<string, any[]>
}

interface ViewDataItem {
  label: string
  value?: string | string[]
  options?: string[] | any[]
  isTitle: boolean
  isWithCount?: boolean
  isHeading?: boolean
}

function isValidDateString(str: string): boolean {
  if (!isNaN(Number(str))) return false
  const date = new Date(str)
  return !isNaN(date.getTime())
}

export const generateBulkOperationsViewData = (
  data: GenerateBulkOperationsJobData,
  bulkUpdateType: string | undefined,
  isViewMode = true,
): ViewDataItem[] => {
  let dataArray: ViewDataItem[] = []
  let commonMeta: ViewDataItem[] = []

  if (isViewMode) {
    commonMeta = [
      { label: 'User Name', value: data?.userName || CONSTANTS.DEFAULT_TEXT, isTitle: false },
      {
        label: 'Start Time',
        value:
          getDateInFormat(data?.scheduleDate ?? '', 'dd/MM/yyyy HH:mm') || CONSTANTS.DEFAULT_TEXT,
        isTitle: false,
      },
      {
        label: 'End Time',
        value: getDateInFormat(data?.endDate ?? '', 'dd/MM/yyyy HH:mm') || CONSTANTS.DEFAULT_TEXT,
        isTitle: false,
      },
      {
        label: 'Success Count',
        value: data?.successCount?.toString() ?? CONSTANTS.DEFAULT_TEXT,
        isTitle: false,
      },
      {
        label: 'Failure Count',
        value: data?.failureCount?.toString() ?? CONSTANTS.DEFAULT_TEXT,
        isTitle: false,
      },
    ]
  }

  if (bulkUpdateType === 'bulk-state-change') {
    const targetStateLabel =
      CONSTANTS.BULK_OPS.TARGET_STATE[
        data?.requestData?.targetState as keyof typeof CONSTANTS.BULK_OPS.TARGET_STATE
      ] || CONSTANTS.DEFAULT_TEXT

    dataArray = [{ label: 'Target State', value: targetStateLabel, isTitle: false }]

    if (isViewMode) {
      dataArray = [
        { label: 'Target State', value: targetStateLabel, isTitle: false },
        { label: 'Job Id', value: data?.jobId || CONSTANTS.DEFAULT_TEXT, isTitle: false },
        {
          label: 'Asset Count',
          value: String(data?.requestData?.blitzIds?.length ?? CONSTANTS.DEFAULT_TEXT),
          isTitle: false,
        },
        ...commonMeta,
        {
          label: 'Blitz Id',
          value: data?.requestData?.blitzIds || CONSTANTS.DEFAULT_TEXT,
          options: (data?.requestData?.blitzIds || []).map((id: string) => ({
            key: id,
            value: id,
          })),
          isTitle: false,
          isWithCount: true,
        },
      ]
    }
  } else if (bulkUpdateType === 'interactive-bulk-update') {
    const attributes = data?.requestData?.attributes || []

    if (isViewMode) {
      dataArray = [
        { label: 'Job Id', value: data?.jobId || CONSTANTS.DEFAULT_TEXT, isTitle: false },
        {
          label: 'Asset Count',
          value: String(data?.requestData?.blitzIds?.length ?? CONSTANTS.DEFAULT_TEXT),
          isTitle: false,
        },
        ...commonMeta,
        {
          label: 'Blitz Id',
          value: data?.requestData?.blitzIds || CONSTANTS.DEFAULT_TEXT,
          options: (data?.requestData?.blitzIds || []).map((id: string) => ({
            key: id,
            value: id,
          })),
          isTitle: false,
          isWithCount: true,
        },
      ]
    }

    if (attributes.length > 0) {
      dataArray.push({ label: 'Applicable Attributes', value: '', isTitle: false, isHeading: true })
      const baseOptionsConfig: any = CONSTANTS.BULK_OPS.UPDATE_TYPE
      attributes.forEach((attribute: any, index: number) => {
        dataArray.push(
          { label: `Attribute ${index + 1}`, value: '', isTitle: false, isHeading: true },
          { label: 'Attribute', value: attribute.fieldLabel, isTitle: false },
          {
            label: 'Update Type',
            value: baseOptionsConfig[attribute.updateBehaviour],
            isTitle: false,
          },
        )
        if (attribute.updateBehaviour !== 'CLEAR_ALL_VALUES') {
          let value = (attribute.value?.key || attribute.value) ?? CONSTANTS.DEFAULT_TEXT
          const options: any = []
          const masterDataWithFieldName = data?.masterDataWithFieldName || {}
          if (attribute.fieldName && masterDataWithFieldName[attribute.fieldName]) {
            const masters = masterDataWithFieldName[attribute.fieldName]
            value.split(', ').forEach((val: string) => {
              const foundOption = masters.find((option: any) => option.key === val)
              if (foundOption) {
                options.push(foundOption)
              }
            })
            value = value.split(', ')
            if (attribute.fieldName === 'packageId') {
              value = attribute.value
            }
          } else if (isValidDateString(value)) {
            value = format(
              parse(value, CONSTANTS.DATE_FORMAT_TIME, new Date()),
              CONSTANTS.DATE_FORMAT_TIME,
            )
          } else if (typeof value === 'object' && value !== null) {
            value = JSON.stringify(value)
          }
          dataArray.push({
            label:
              CONSTANTS.BULK_OPS.INTERACTIVE_VALUE_LABEL[
                attribute.updateBehaviour as keyof typeof CONSTANTS.BULK_OPS.INTERACTIVE_VALUE_LABEL
              ],
            value,
            options,
            isTitle: false,
            isWithCount: true,
          })
        }
      })
    }
  } else if (bulkUpdateType === 'bulk-transcoding') {
    let fileTypeValue: string = CONSTANTS.DEFAULT_TEXT
    if (data?.requestData?.processType !== '') {
      fileTypeValue =
        data?.requestData?.processType === 'INTEGRATED' ? 'Integrated' : 'Non-Integrated'
    }
    dataArray = [{ label: 'File Type', value: fileTypeValue, isTitle: false }]

    if (isViewMode) {
      dataArray = [
        ...dataArray,
        { label: 'Job Id', value: data?.jobId || CONSTANTS.DEFAULT_TEXT, isTitle: false },
        {
          label: 'Asset Count',
          value: data?.assetCount?.toString() ?? CONSTANTS.DEFAULT_TEXT,
          isTitle: false,
        },
        ...commonMeta,
      ]
    }
  } else {
    dataArray = [
      {
        label: 'Attributes',
        value: data?.attributes?.map((att) => att.label).join(', ') || CONSTANTS.DEFAULT_TEXT,
        isTitle: false,
      },
    ]

    if (isViewMode) {
      dataArray = [
        ...dataArray,
        { label: 'Job Id', value: data?.jobId || CONSTANTS.DEFAULT_TEXT, isTitle: false },
        {
          label: 'Asset Count',
          value: data?.assetCount?.toString() ?? CONSTANTS.DEFAULT_TEXT,
          isTitle: false,
        },
        ...commonMeta,
      ]
    }
  }
  return dataArray
}

const getJobStatus = (status: string) => {
  return (
    <div className={`${Style['bulk-update-status']} ${Style[status]}`}>{bulkStatus(status)}</div>
  )
}
export default getJobStatus
export const bulkStatus = (status: string) => {
  if (status === 'IN_PROGRESS') {
    return 'In Progress'
  } else if (status === 'COMPLETED') {
    return 'Completed'
  } else if (status === 'SCHEDULED') {
    return 'Scheduled'
  } else if (status === 'IN_QUEUE') {
    return 'In Queue'
  } else {
    return status
  }
}
export const isCancellableStatus = (status: any) =>
  status === 'IN_PROGRESS' || status === 'IN_QUEUE' || status === 'SCHEDULED'

export const bulkUpdateListingAction = (data: any, dispatch: any, showCancel = true) => {
  const cancelCondition: any[] = ['IN_PROGRESS', 'IN_QUEUE', 'SCHEDULED']
  return (
    <span className='d-flex allign-items-center mbn-7'>
      <MUITooltip
        title={data.status === 'COMPLETED' ? 'Download Report' : ''}
        disableInteractive={true}
      >
        <span
          onClick={(e: any) => {
            e.preventDefault()
            e.stopPropagation()
            if (isJobCancelledBeforeStart(data)) {
              dispatch(showAlert([true, CONSTANTS.BULK_UPDATE_CANCELLED_JOB, 'error']))
              return
            }
            const updatedData = {
              ...data,
              jobType: data?.jobType === 'bulk-transcode' ? 'bulk-update' : data?.jobType,
            }
            data.status === 'COMPLETED' && downloadBulkUpdateDataFunction(updatedData, dispatch)
          }}
          data-testid='download-id'
          style={{ opacity: data.status !== 'COMPLETED' ? 0.5 : 1 }}
        >
          <DownloadIcon />
        </span>
      </MUITooltip>
      &nbsp;&nbsp;&nbsp;
      {showCancel ? (
        <MUITooltip
          title={['IN_PROGRESS', 'IN_QUEUE', 'SCHEDULED'].includes(data.status) ? 'Cancel Job' : ''}
          disableInteractive={true}
        >
          <span
            data-testid='cancel-id'
            onClick={(e: any) => {
              e.preventDefault()
              e.stopPropagation()
              if (
                data.status === 'IN_PROGRESS' ||
                data.status === 'IN_QUEUE' ||
                data.status === 'SCHEDULED'
              ) {
                dispatch(setcancelBulkUpdate({ show: true, data: data || {} }) as any)
              }
            }}
            style={{ opacity: cancelCondition?.includes(data.status) ? 1 : 0.5 }}
          >
            <BulkUploadCancelIcon />
          </span>
        </MUITooltip>
      ) : (
        <></>
      )}
    </span>
  )
}

export const applicableAttributesUpdateTypes = (attribute: any) => {
  const baseOptionsConfig: any = CONSTANTS.BULK_OPS.UPDATE_TYPE
  const baseOptions = Object.keys(baseOptionsConfig).map((key) => ({
    key,
    label: baseOptionsConfig[key],
  }))

  const { fieldType, additionalProperties, fieldName, dataType, isMultiple } = attribute || {}

  if (
    (fieldType === CONSTANTS.FIELDTYPES.TEXT && additionalProperties?.tagInput) ||
    (fieldType === CONSTANTS.FIELDTYPES.NUMBER && additionalProperties?.tagInput) ||
    (fieldType === CONSTANTS.FIELDTYPES.TEXTAREA && additionalProperties?.tagInput) ||
    (fieldType === CONSTANTS.FIELDTYPES.SELECT &&
      isMultiple &&
      dataType !== CONSTANTS.FIELDTYPES.BOOLEAN) ||
    fieldName === 'parentId'
  ) {
    return baseOptions
  }

  if (fieldType === CONSTANTS.FIELDTYPES.COMPLEXTYPE) {
    return [baseOptions[0]] // Only CLEAR_ALL_VALUES for complex types
  }

  return baseOptions.slice(0, 2) // CLEAR_ALL_VALUES, OVERWRITE_WITH_NEW_VALUES
}

export const getBulkUpdatePath = (path: any) => {
  if (path === 'BULK_STATE_CHANGE') return 'bulk-state-job'
  if (path === 'INTERACTIVE_BULK_UPDATE') return 'bulk-interactive-job'
  if (path === 'BULK_UPDATE') return 'bulk-upload-job'
  return ''
}

export const getBulkUpdateCancelPath = (path: any) => {
  if (['interactive-bulk-update', 'INTERACTIVE_BULK_UPDATE'].includes(path))
    return 'bulk-interactive'
  if (path === 'bulk-update_transcoding') {
    return 'bulk-update'
  }
  return path
}

export const getBulkUpdateListPath = (path: any) => {
  if (path === 'BULK_STATE_CHANGE') return 'bulk-state-jobs'
  if (path === 'INTERACTIVE_BULK_UPDATE') return 'bulk-interactive-jobs'
  if (path === 'BULK_UPDATE') return 'bulk-upload-jobs'
  return ''
}

const isBulkTranscoding = (backButtonUrl: string | undefined) => {
  const splitUrl = backButtonUrl?.split('/') || []
  const bulkUpdateType = splitUrl[splitUrl.length - 1]
  return bulkUpdateType === 'bulk-transcoding'
}

export const fetchBulkUpdateTypeDetails = async (id: any, bulkOpsConfig: any, dispatch: any) => {
  const jobDetailsData: any = []
  if (isBulkTranscoding(bulkOpsConfig?.backButtonUrl)) {
    const reqData = API_CONFIG(API_LIST.BULK_APIS.TRANSCODING_JOB_VIEW, null, {
      transcodingJobId: id,
    })
    jobDetailsData.push(mainApiService(reqData))
  } else {
    jobDetailsData.push(
      mainApiService(
        API_CONFIG(API_LIST.BULK_UPLOAD.GET_JOB_DETAILS, null, {
          jobId: id,
          jobType: getBulkUpdatePath(bulkOpsConfig.tooltipModule),
        }),
      ),
    )
  }
  const attrApiRequest = API_CONFIG(
    API_LIST.ATTRIBUTE_REPOSITORY.LIST_ATTRIBUTE_REPO,
    null,
    null,
    `isActive=1&limit=${CONSTANTS.API_DEFAULT_LENGTH}&isBulkUpdate=true`,
  )
  if (bulkOpsConfig.tooltipModule === CONFIG.BULK_OPS_TYPES.INTERACTIVE_BULK_UPDATE) {
    jobDetailsData.push(mainApiService(attrApiRequest))
  }
  const [jobDetails, attributeList] = await Promise.all(jobDetailsData)
  const masterDataWithFieldName: any = {}
  if (jobDetails?.data?.requestData?.attributes?.length) {
    if (attributeList?.data?.records) {
      jobDetails.data.requestData.attributes.forEach((attr: any) => {
        const selectedAttribute = attributeList.data.records.find(
          (item: any) => item.fieldName === attr.fieldName,
        )
        if (
          selectedAttribute &&
          selectedAttribute.master &&
          selectedAttribute.fieldType === CONSTANTS.FIELDTYPES.SELECT
        ) {
          masterDataWithFieldName[selectedAttribute.fieldName] = selectedAttribute.master
        }
      })
      if (Object.keys(masterDataWithFieldName).length > 0) {
        const masterRes = await dispatch(
          fetchMasterData({
            masters: Object.values(masterDataWithFieldName).map((item: any) =>
              item.replace('SystemConfigurations/', '').toUpperCase(),
            ),
            showLoader: false,
          }) as any,
        )
        for (const key in masterDataWithFieldName) {
          const masterKey = masterDataWithFieldName[key]
            .replace('SystemConfigurations/', '')
            .toUpperCase()
          masterDataWithFieldName[key] = masterRes?.payload?.data?.[masterKey] || []
        }
      }
    }
    if (jobDetails?.data) {
      jobDetails.data['masterDataWithFieldName'] = masterDataWithFieldName
    }
  }
  return jobDetails
}
